import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, MenuItem, Select, Card, Autocomplete, TextField } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as XLSX from 'xlsx';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { getAccounts } from '../../../sp-firebase/accountFB';
import { Account } from '../../../sp-types/accountTypes';
import { toast } from 'react-toastify';
import { triggerExposureCalc } from '../../../hooks/useExposure';
import { Timestamp } from 'firebase/firestore';
import { saveInventoryReport } from '../../../sp-firebase/inventoryReportFB';
import { createFutureOrder } from '../../../sp-firebase/futureOrderFB';
import { MonthYearValue } from '../../../utils/MonthYear/MonthYearValue';
import { IMassUnit } from '../../../sp-types/massUnitType';
import { MassUnitsDict } from '../../../utils/MassUnits/massUnitsUtils';
import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { SaveProgressDialog } from './SaveProgressDialog';
import { LoadingSpinnerOverlay } from '../../../components/UI-Elements/LoadingSpinnerOverlay';
import { OverlayDisplayDiv } from '../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { ClearDataPopup } from './ClearDataPopup';

type UploadType = 'future-orders' | 'mrp' | 'pos';

interface ExcelRow {
  [key: string]: any;
}

interface ColumnMapping {
  [excelColumn: string]: string;
}

interface MaterialMapping {
  [excelMaterial: string]: string;
}

interface CurrencyMapping {
  [excelCurrency: string]: string;
}

interface UnitMapping {
  [excelUnit: string]: IMassUnit | 'kg';
}

// Excel serial date to JavaScript Date
const excelDateToJSDate = (serial: number): Date => {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate());
};

// Check if value looks like Excel serial date
const isExcelDate = (value: any): boolean => {
  return typeof value === 'number' && value > 40000 && value < 60000;
};

// Format date as yyyy-MM-dd string
const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FUTURE_ORDER_COLUMNS = [
  'materialName',
  'materialId',
  'customerName',
  'month1',
  'month2',
  'month3',
  'month4',
  'month5',
  'month6',
  'month7',
  'month8',
  'month9',
  'month10',
  'month11',
  'month12',
  'month13',
  'month14',
  'month15',
  'month16',
  'month17',
  'month18',
  'month19',
  'month20',
  'month21',
  'month22',
  'month23',
  'month24',
];

const PO_COLUMNS = [
  'poId',
  'batchId',
  'materialName',
  'productId',
  'expectedDate',
  'quantity',
  'unit',
  'deliveredQuantity',
  'actualPrice',
  'actualCurrency',
  'isRatio',
  'ratioNum',
  'supplier',
];

const MRP_COLUMNS = ['materialName', 'materialId', 'customerName', ...FUTURE_ORDER_COLUMNS.slice(3)];

export const ExcelUploadTab: React.FC = () => {
  const { materialsMap } = useMaterialsStore();
  const { account } = useAccountStore();

  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [uploadType, setUploadType] = useState<UploadType>('future-orders');
  const [file, setFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [materialMapping, setMaterialMapping] = useState<MaterialMapping>({});
  const [currencyMapping, setCurrencyMapping] = useState<CurrencyMapping>({});
  const [unitMapping, setUnitMapping] = useState<UnitMapping>({});
  const [loading, setLoading] = useState(false);
  const [materialFilter, setMaterialFilter] = useState<string>('');
  const [previewPage, setPreviewPage] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [batchIdEdits, setBatchIdEdits] = useState<{ [rowNum: number]: string }>({});
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [workbookData, setWorkbookData] = useState<XLSX.WorkBook | null>(null);
  const [saveProgressOpen, setSaveProgressOpen] = useState(false);
  const [saveProgress, setSaveProgress] = useState<{ message: string; success: boolean }[]>([]);
  const [savingComplete, setSavingComplete] = useState(false);
  const [customerNameEdits, setCustomerNameEdits] = useState<{ [rowNum: number]: string }>({});
  const [priceEdits, setPriceEdits] = useState<{ [rowNum: number]: number }>({});
  const [showClearDataPopup, setShowClearDataPopup] = useState(false);
  const ROWS_PER_PAGE = 50;

  // Separate localStorage keys to avoid quota issues
  const STORAGE_KEYS = {
    metadata: 'excelUpload_metadata',
    excelData: 'excelUpload_data',
    mappings: 'excelUpload_mappings',
  };

  // Clear all localStorage data
  const clearAllLocalStorage = () => {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  };

  // Load accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      const accountsList = await getAccounts();
      if (accountsList) {
        setAccounts(accountsList);
        // Set default to current account if available
        if (account?.id) {
          setSelectedAccountId(account.id);
        }
      }
    };
    loadAccounts();
  }, [account]);

  // Restore state from localStorage on mount
  useEffect(() => {
    try {
      const metadata = localStorage.getItem(STORAGE_KEYS.metadata);
      const excelDataStr = localStorage.getItem(STORAGE_KEYS.excelData);
      const mappings = localStorage.getItem(STORAGE_KEYS.mappings);

      if (metadata) {
        const parsed = JSON.parse(metadata);
        if (parsed.step) setStep(parsed.step);
        if (parsed.uploadType) setUploadType(parsed.uploadType);
        if (parsed.headers) setHeaders(parsed.headers);
        if (parsed.fileName) {
          const mockFile = new File([''], parsed.fileName, { type: parsed.fileType || 'application/octet-stream' });
          setFile(mockFile);
        }
      }

      if (excelDataStr) {
        const parsed = JSON.parse(excelDataStr);
        if (parsed.excelData) setExcelData(parsed.excelData);
      }

      if (mappings) {
        const parsed = JSON.parse(mappings);
        if (parsed.columnMapping) setColumnMapping(parsed.columnMapping);
        if (parsed.materialMapping) setMaterialMapping(parsed.materialMapping);
        if (parsed.currencyMapping) setCurrencyMapping(parsed.currencyMapping);
        if (parsed.unitMapping) setUnitMapping(parsed.unitMapping);
      }
    } catch (error) {
      console.error('Error restoring state from localStorage:', error);
      clearAllLocalStorage();
    }
  }, []);

  // Save state to localStorage whenever it changes (split to avoid quota)
  useEffect(() => {
    if (excelData.length > 0 || Object.keys(columnMapping).length > 0) {
      try {
        // Save metadata (small data)
        const metadata = {
          step,
          uploadType,
          headers,
          fileName: file?.name,
          fileType: file?.type,
        };
        localStorage.setItem(STORAGE_KEYS.metadata, JSON.stringify(metadata));

        // Save excel data separately (potentially large)
        localStorage.setItem(STORAGE_KEYS.excelData, JSON.stringify({ excelData }));

        // Save mappings separately
        const mappings = {
          columnMapping,
          materialMapping,
          currencyMapping,
          unitMapping,
        };
        localStorage.setItem(STORAGE_KEYS.mappings, JSON.stringify(mappings));
      } catch (error) {
        console.error('Error saving to localStorage:', error);
        toast.error('Failed to save progress. Data may be too large.');
      }
    }
  }, [step, uploadType, excelData, headers, columnMapping, materialMapping, currencyMapping, unitMapping, file]);

  const availableColumns =
    uploadType === 'pos' ? PO_COLUMNS : uploadType === 'mrp' ? MRP_COLUMNS : FUTURE_ORDER_COLUMNS;

  // Available mass units including KG
  const availableMassUnits: (IMassUnit | 'kg')[] = [...(Object.keys(MassUnitsDict) as IMassUnit[]), 'kg'];

  // Conversion for KG to metric ton
  const convertUnitToMetricTon = (value: number, unit: IMassUnit | 'kg'): number => {
    if (unit === 'kg') {
      return value * 0.001;
    }
    if (unit in MassUnitsDict) {
      return MassUnitsDict[unit as IMassUnit].convertPrice(value);
    }
    return value;
  };

  // Auto-map month columns based on date headers
  const autoMapMonthColumns = (headers: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {};
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11

    // Only auto-map for future-orders or mrp upload types
    if (uploadType !== 'future-orders' && uploadType !== 'mrp') {
      return mapping;
    }

    headers.forEach((header) => {
      // Check if header is a date string (yyyy-MM-dd format)
      const dateMatch = header.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateMatch) {
        const headerYear = parseInt(dateMatch[1], 10);
        const headerMonth = parseInt(dateMatch[2], 10) - 1; // Convert to 0-11

        // Calculate months difference from current month
        const monthsDiff = (headerYear - currentYear) * 12 + (headerMonth - currentMonth);

        // Map to month1, month2, etc. (monthsDiff + 1 because month1 is current month)
        if (monthsDiff >= 0 && monthsDiff < 24) {
          const monthColumn = `month${monthsDiff + 1}`;
          if (FUTURE_ORDER_COLUMNS.includes(monthColumn)) {
            mapping[header] = monthColumn;
          }
        }
      }
    });

    return mapping;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    clearAllLocalStorage(); // Clear previous data when uploading new file
    setFile(uploadedFile);
    setLoading(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        let jsonData: any[][] = [];

        if (uploadedFile.name.endsWith('.csv')) {
          const text = data as string;
          const lines = text.split('\n').filter((line) => line.trim());
          jsonData = lines.map((line) => line.split(',').map((cell) => cell.trim()));
          setSheetNames([]);
          setSelectedSheet('');
          setWorkbookData(null);
        } else {
          const workbook = XLSX.read(data, { type: 'binary' });
          setWorkbookData(workbook);
          setSheetNames(workbook.SheetNames);
          const sheetName = workbook.SheetNames[0];
          setSelectedSheet(sheetName);
          const worksheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        }

        if (jsonData.length > 0) {
          const headers = jsonData[0].map((header: any) => {
            // Convert Excel date headers to date strings
            if (isExcelDate(header)) {
              return formatDateString(excelDateToJSDate(header));
            }
            return String(header);
          });
          const rows = jsonData.slice(1).map((row) => {
            const rowData: ExcelRow = {};
            headers.forEach((header, index) => {
              let value = row[index];
              // Convert Excel date numbers to JS dates
              if (isExcelDate(value)) {
                value = excelDateToJSDate(value);
              }
              rowData[header] = value;
            });
            return rowData;
          });

          setHeaders(headers);
          setExcelData(rows);
          // Auto-map month columns based on date headers
          const autoMapping = autoMapMonthColumns(headers);
          setColumnMapping(autoMapping);
          // Stay on upload step to show preview
        }
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Error reading file');
      } finally {
        setLoading(false);
      }
    };

    if (uploadedFile.name.endsWith('.csv')) {
      reader.readAsText(uploadedFile);
    } else {
      reader.readAsBinaryString(uploadedFile);
    }
  };

  const handleSheetChange = (sheetName: string) => {
    if (!workbookData) return;

    setSelectedSheet(sheetName);
    setLoading(true);

    try {
      const worksheet = workbookData.Sheets[sheetName];
      const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length > 0) {
        const headers = jsonData[0].map((header: any) => {
          // Convert Excel date headers to date strings
          if (isExcelDate(header)) {
            return formatDateString(excelDateToJSDate(header));
          }
          return String(header);
        });
        const rows = jsonData.slice(1).map((row) => {
          const rowData: ExcelRow = {};
          headers.forEach((header, index) => {
            let value = row[index];
            // Convert Excel date numbers to JS dates
            if (isExcelDate(value)) {
              value = excelDateToJSDate(value);
            }
            rowData[header] = value;
          });
          return rowData;
        });

        setHeaders(headers);
        setExcelData(rows);
        // Auto-map month columns based on date headers when sheet changes
        const autoMapping = autoMapMonthColumns(headers);
        setColumnMapping(autoMapping);
      }
    } catch (error) {
      console.error('Error reading sheet:', error);
      toast.error('Error reading sheet');
    } finally {
      setLoading(false);
    }
  };

  const getMaterialMappingNeeded = (): string[] => {
    const uniqueMaterials = new Set<string>();
    const materialCol = Object.keys(columnMapping).find((key) => columnMapping[key] === 'materialName');

    if (materialCol) {
      excelData.forEach((row) => {
        if (row[materialCol]) {
          uniqueMaterials.add(row[materialCol]);
        }
      });
    }

    return Array.from(uniqueMaterials).filter((mat) => materialMapping[mat] !== 'SKIP');
  };

  const getUnitMappingNeeded = (): string[] => {
    const uniqueUnits = new Set<string>();
    const unitCol = Object.keys(columnMapping).find((key) => columnMapping[key] === 'unit');

    if (unitCol) {
      excelData.forEach((row) => {
        if (row[unitCol]) {
          uniqueUnits.add(row[unitCol]);
        }
      });
    }

    return Array.from(uniqueUnits);
  };

  const getCurrencyMappingNeeded = (): string[] => {
    const uniqueCurrencies = new Set<string>();
    const currencyCol = Object.keys(columnMapping).find((key) => columnMapping[key] === 'actualCurrency');

    if (currencyCol) {
      excelData.forEach((row) => {
        if (row[currencyCol]) {
          uniqueCurrencies.add(row[currencyCol]);
        }
      });
    }

    return Array.from(uniqueCurrencies);
  };

  const processData = () => {
    if (uploadType === 'pos') {
      return processPOData();
    } else if (uploadType === 'mrp') {
      return processMRPData();
    } else {
      return processFutureOrderData();
    }
  };

  const processPOData = () => {
    const result: any[] = [];
    const poIdCounter: { [poId: string]: number } = {};

    excelData.forEach((row, index) => {
      const mappedRow: any = { rowNum: index + 1 };

      Object.keys(columnMapping).forEach((excelCol) => {
        const targetCol = columnMapping[excelCol];
        const value = row[excelCol];

        // Convert dates if this is expectedDate column
        if (targetCol === 'expectedDate' && value instanceof Date) {
          mappedRow[targetCol] = value;
        } else if (targetCol === 'expectedDate' && isExcelDate(value)) {
          mappedRow[targetCol] = excelDateToJSDate(value);
        } else {
          mappedRow[targetCol] = value;
        }
      });

      // Map material
      if (mappedRow.materialName && materialMapping[mappedRow.materialName]) {
        const systemMaterial = materialMapping[mappedRow.materialName];
        // Skip materials marked as SKIP
        if (systemMaterial === 'SKIP') {
          return;
        }
        mappedRow.materialId = systemMaterial;
      } else {
        // Skip rows without material mapping
        return;
      }

      // Auto-generate batchId for duplicate POIDs
      if (mappedRow.poId) {
        if (!poIdCounter[mappedRow.poId]) {
          poIdCounter[mappedRow.poId] = 1;
        } else {
          poIdCounter[mappedRow.poId]++;
        }

        // Add batch ID for all occurrences (1 for first, 2 for second, etc.)
        if (!mappedRow.batchId) {
          mappedRow.batchId = String(poIdCounter[mappedRow.poId]);
        }

        // Apply manual edits if any
        if (batchIdEdits[mappedRow.rowNum]) {
          mappedRow.batchId = batchIdEdits[mappedRow.rowNum];
        }
      }

      // Map currency
      if (mappedRow.actualCurrency && currencyMapping[mappedRow.actualCurrency]) {
        mappedRow.actualCurrency = currencyMapping[mappedRow.actualCurrency];
      }

      // Map unit and calculate converted quantity
      if (mappedRow.unit && unitMapping[mappedRow.unit]) {
        const systemUnit = unitMapping[mappedRow.unit];
        mappedRow.systemUnit = systemUnit;
        if (mappedRow.quantity) {
          mappedRow.convertedQuantity = convertUnitToMetricTon(Number(mappedRow.quantity), systemUnit);
        }
      }

      result.push(mappedRow);
    });

    return result;
  };

  const processMRPData = () => {
    const result: any[] = [];

    excelData.forEach((row, index) => {
      const mappedRow: any = { rowNum: index + 1 };

      Object.keys(columnMapping).forEach((excelCol) => {
        const targetCol = columnMapping[excelCol];
        mappedRow[targetCol] = row[excelCol];
      });

      // Map material
      if (mappedRow.materialName && materialMapping[mappedRow.materialName]) {
        const systemMaterial = materialMapping[mappedRow.materialName];
        // Skip materials marked as SKIP
        if (systemMaterial === 'SKIP') {
          return;
        }
        mappedRow.materialId = systemMaterial;
      } else {
        // Skip rows without material mapping
        return;
      }

      // Apply customer name edits
      if (customerNameEdits[mappedRow.rowNum]) {
        mappedRow.customerName = customerNameEdits[mappedRow.rowNum];
      }

      // Apply price edits or get default from materialsMap
      if (priceEdits[mappedRow.rowNum]) {
        mappedRow.pricePerUnit = priceEdits[mappedRow.rowNum];
      } else if (materialsMap[mappedRow.materialId]?.currentPrice) {
        mappedRow.pricePerUnit = Math.floor(materialsMap[mappedRow.materialId].currentPrice);
      }

      result.push(mappedRow);
    });

    return result;
  };

  const processFutureOrderData = () => {
    const result: any[] = [];

    excelData.forEach((row, index) => {
      const mappedRow: any = { rowNum: index + 1 };

      Object.keys(columnMapping).forEach((excelCol) => {
        const targetCol = columnMapping[excelCol];
        mappedRow[targetCol] = row[excelCol];
      });

      // Map material
      if (mappedRow.materialName && materialMapping[mappedRow.materialName]) {
        const systemMaterial = materialMapping[mappedRow.materialName];
        // Skip materials marked as SKIP
        if (systemMaterial === 'SKIP') {
          return;
        }
        mappedRow.materialId = systemMaterial;
      } else {
        // Skip rows without material mapping
        return;
      }

      // Apply customer name edits
      if (customerNameEdits[mappedRow.rowNum]) {
        mappedRow.customerName = customerNameEdits[mappedRow.rowNum];
      }

      // Apply price edits or get default from materialsMap
      if (priceEdits[mappedRow.rowNum]) {
        mappedRow.pricePerUnit = priceEdits[mappedRow.rowNum];
      } else if (materialsMap[mappedRow.materialId]?.currentPrice) {
        mappedRow.pricePerUnit = Math.floor(materialsMap[mappedRow.materialId].currentPrice);
      }

      result.push(mappedRow);
    });

    return result;
  };

  const handleSave = async () => {
    const processedData = processData();

    if (processedData.length === 0) {
      toast.error('No data to save');
      return;
    }

    // Reset progress tracking
    setSaveProgress([]);
    setSavingComplete(false);
    setSaveProgressOpen(true);
    setLoading(true);

    try {
      if (uploadType === 'pos') {
        await savePOs(processedData);
      } else if (uploadType === 'mrp') {
        await saveMRP(processedData);
      } else {
        await saveFutureOrders(processedData);
      }

      setSavingComplete(true);

      // Clear localStorage after successful save
      clearAllLocalStorage();

      // Delay reset to allow user to see final results
      setTimeout(() => {
        setStep('upload');
        setFile(null);
        setExcelData([]);
        setHeaders([]);
        setColumnMapping({});
        setMaterialMapping({});
        setCurrencyMapping({});
        setUnitMapping({});
        setCustomerNameEdits({});
        setPriceEdits({});
        setSaveProgressOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveProgress((prev) => [...prev, { message: `Fatal error: ${error}`, success: false }]);
      setSavingComplete(true);
    } finally {
      setLoading(false);
    }
  };

  const savePOs = async (data: any[]) => {
    if (!selectedAccountId) {
      toast.error('Please select an account');
      setSaveProgressOpen(false);
      return;
    }

    const materialIds = new Set<string>();
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < data.length; i++) {
      const po = data[i];
      try {
        // Use converted quantity instead of raw quantity
        const quantity = po.convertedQuantity || po.quantity;

        materialIds.add(po.materialId);

        let reportDate = po.expectedDate ? new Date(po.expectedDate) : new Date();

        // If date is in the past, set to current month
        const now = new Date();

        if (reportDate < new Date(now.getFullYear(), now.getMonth(), 1)) {
          reportDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        // Check if price is less than 9 - likely a ratio instead of actual price
        const rawPrice = Number(po.actualPrice) || 0;
        const isRatioPrice = rawPrice > 0 && rawPrice < 9;

        // Calculate price per ton: if unit is kg, multiply by 1000
        const pricePerTon = !isRatioPrice && po.systemUnit === 'kg' ? rawPrice * 1000 : rawPrice;

        const inventoryReport = {
          accountId: selectedAccountId,
          materialId: po.materialId,
          quantity: Number(quantity) || 0,
          suppliedQty: Number(po.deliveredQuantity) || 0,
          ratio: isRatioPrice ? rawPrice : Number(po.ratioNum) || 0,
          reportingTime: Timestamp.fromDate(reportDate),
          monthYear: { month: reportDate.getMonth(), year: reportDate.getFullYear() },
          status: 'completed' as const,
          isPO: true,
          poId: po.poId || '',
          poBatchId: po.batchId || '',
          actualPrice: isRatioPrice ? 0 : pricePerTon,
          currency: po.actualCurrency || 'USD',
          isRatio: isRatioPrice,
          key: 2,
          supplier: po.supplier || '',
        };

        const result = await saveInventoryReport(inventoryReport, true);
        if (result?.error) {
          throw new Error(result.error);
        }

        successCount++;
        setSaveProgress((prev) => [
          ...prev,
          { message: `✓ PO ${po.poId} - Batch ${po.batchId} saved (${i + 1}/${data.length})`, success: true },
        ]);
      } catch (error) {
        errorCount++;
        setSaveProgress((prev) => [
          ...prev,
          { message: `✗ PO ${po.poId} - Batch ${po.batchId} failed: ${error}`, success: false },
        ]);
      }
    }

    // Trigger exposure calculation
    try {
      await triggerExposureCalc({
        accountId: selectedAccountId,
        materialIds: Array.from(materialIds),
      });
      setSaveProgress((prev) => [...prev, { message: '✓ Exposure calculation triggered', success: true }]);
    } catch (error) {
      setSaveProgress((prev) => [...prev, { message: `✗ Exposure calculation failed: ${error}`, success: false }]);
    }

    setSaveProgress((prev) => [
      ...prev,
      {
        message: `\nCompleted: ${successCount} successful, ${errorCount} failed out of ${data.length} total`,
        success: errorCount === 0,
      },
    ]);
  };

  const saveMRP = async (data: any[]) => {
    if (!selectedAccountId) {
      toast.error('Please select an account');
      setSaveProgressOpen(false);
      return;
    }

    const materialIds = new Set<string>();
    const currentDate = new Date();
    let successCount = 0;
    let errorCount = 0;
    let totalOrdersToCreate = 0;

    // First, count total orders to create (one per non-zero month per row)
    for (const mrp of data) {
      FUTURE_ORDER_COLUMNS.slice(3).forEach((col) => {
        const monthValue = Number(mrp[col]) || 0;
        if (monthValue > 0) totalOrdersToCreate++;
      });
    }

    setSaveProgress((prev) => [
      ...prev,
      { message: `Creating ${totalOrdersToCreate} individual MRP orders (one per month)...`, success: true },
    ]);

    let orderIndex = 0;
    for (let i = 0; i < data.length; i++) {
      const mrp = data[i];
      materialIds.add(mrp.materialId);

      // Create one future order per month with non-zero value
      for (let monthIdx = 0; monthIdx < FUTURE_ORDER_COLUMNS.slice(3).length; monthIdx++) {
        const col = FUTURE_ORDER_COLUMNS.slice(3)[monthIdx];
        const monthValue = Number(mrp[col]) || 0;

        if (monthValue <= 0) continue; // Skip months with no value

        orderIndex++;
        const targetDate = new Date(currentDate);
        targetDate.setMonth(currentDate.getMonth() + monthIdx);

        const monthDate = {
          month: targetDate.getMonth() + 1,
          year: targetDate.getFullYear(),
        };

        const singleMonthExposure: MonthYearValue = {
          value: monthValue,
          date: monthDate,
        };

        // Build cumulative totalExposure from current month to delivery month
        // Exposure exists from now until the delivery date
        const totalExposureCalendar: MonthYearValue[] = [];
        for (let m = 0; m <= monthIdx; m++) {
          const expDate = new Date(currentDate);
          expDate.setMonth(currentDate.getMonth() + m);
          totalExposureCalendar.push({
            value: monthValue,
            date: {
              month: expDate.getMonth() + 1,
              year: expDate.getFullYear(),
            },
          });
        }

        try {
          const futureOrder: Partial<IFutureOrder> = {
            orderId: `MRP-${mrp.materialId}-${monthDate.year}${String(monthDate.month).padStart(2, '0')}-${Date.now()}`,
            productId: mrp.materialId,
            fileUrl: [],
            clientName: mrp.customerName || '',
            futureOrderDate: Timestamp.now(),
            orderDetails: {
              deliveryType: 'MRP',
              quantity: monthValue,
              deliveryDates: [monthDate],
              productDeliveryCalendar: [singleMonthExposure],
              pricePerUnit: mrp.pricePerUnit || 0,
              currency: 'USD',
            },
            clientExposure: {},
            supplierExposure: [
              {
                materialId: mrp.materialId,
                weightInProduct: 1,
                basePrice: 0,
                supplyLeadTime: 0,
                adjustment: 'none',
                exposureCalendar: [singleMonthExposure],
              },
            ],
            status: 'completed',
            updatedAt: Timestamp.now(),
            totalExposure: [
              {
                materialId: mrp.materialId,
                exposureCalendar: totalExposureCalendar,
              },
            ],
            monthExposure: { [mrp.materialId]: [singleMonthExposure] },
          };

          const result = await createFutureOrder(selectedAccountId, futureOrder);
          if (result?.error) {
            throw new Error(result.error);
          }

          successCount++;
          setSaveProgress((prev) => [
            ...prev,
            {
              message: `✓ MRP ${mrp.materialId} ${monthDate.month}/${monthDate.year} saved (${orderIndex}/${totalOrdersToCreate})`,
              success: true,
            },
          ]);
        } catch (error) {
          errorCount++;
          setSaveProgress((prev) => [
            ...prev,
            {
              message: `✗ MRP ${mrp.materialId} ${monthDate.month}/${monthDate.year} failed: ${error}`,
              success: false,
            },
          ]);
        }
      }
    }

    // Trigger exposure calculation
    try {
      await triggerExposureCalc({
        accountId: selectedAccountId,
        materialIds: Array.from(materialIds),
      });
      setSaveProgress((prev) => [...prev, { message: '✓ Exposure calculation triggered', success: true }]);
    } catch (error) {
      setSaveProgress((prev) => [...prev, { message: `✗ Exposure calculation failed: ${error}`, success: false }]);
    }

    setSaveProgress((prev) => [
      ...prev,
      {
        message: `\nCompleted: ${successCount} successful, ${errorCount} failed out of ${totalOrdersToCreate} total orders`,
        success: errorCount === 0,
      },
    ]);
  };

  const saveFutureOrders = async (data: any[]) => {
    if (!selectedAccountId) {
      toast.error('Please select an account');
      setSaveProgressOpen(false);
      return;
    }

    const materialIds = new Set<string>();
    const currentDate = new Date();
    let successCount = 0;
    let errorCount = 0;
    let totalOrdersToCreate = 0;

    // First, count total orders to create (one per non-zero month per row)
    for (const order of data) {
      FUTURE_ORDER_COLUMNS.slice(3).forEach((col) => {
        const monthValue = Number(order[col]) || 0;
        if (monthValue > 0) totalOrdersToCreate++;
      });
    }

    setSaveProgress((prev) => [
      ...prev,
      { message: `Creating ${totalOrdersToCreate} individual Future Orders (one per month)...`, success: true },
    ]);

    let orderIndex = 0;
    for (let i = 0; i < data.length; i++) {
      const order = data[i];
      materialIds.add(order.materialId);

      // Start from current month (not past months)
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

      // Create one future order per month with non-zero value
      for (let monthIdx = 0; monthIdx < FUTURE_ORDER_COLUMNS.slice(3).length; monthIdx++) {
        const col = FUTURE_ORDER_COLUMNS.slice(3)[monthIdx];
        const monthValue = Number(order[col]) || 0;

        if (monthValue <= 0) continue; // Skip months with no value

        orderIndex++;
        const targetDate = new Date(startDate);
        targetDate.setMonth(startDate.getMonth() + monthIdx);

        const monthDate = {
          month: targetDate.getMonth() + 1,
          year: targetDate.getFullYear(),
        };

        const singleMonthExposure: MonthYearValue = {
          value: monthValue,
          date: monthDate,
        };

        // Build cumulative totalExposure from current month to delivery month
        // Exposure exists from now until the delivery date
        const totalExposureCalendar: MonthYearValue[] = [];
        for (let m = 0; m <= monthIdx; m++) {
          const expDate = new Date(startDate);
          expDate.setMonth(startDate.getMonth() + m);
          totalExposureCalendar.push({
            value: monthValue,
            date: {
              month: expDate.getMonth() + 1,
              year: expDate.getFullYear(),
            },
          });
        }

        try {
          const futureOrder: Partial<IFutureOrder> = {
            orderId: `SC-${order.customerName || order.materialId}-${monthDate.year}${String(monthDate.month).padStart(
              2,
              '0',
            )}-${Date.now()}`,
            productId: order.materialId,
            fileUrl: [],
            clientName: order.customerName || '',
            futureOrderDate: Timestamp.now(),
            orderDetails: {
              deliveryType: 'Future Order',
              quantity: monthValue,
              deliveryDates: [monthDate],
              productDeliveryCalendar: [singleMonthExposure],
              pricePerUnit: order.pricePerUnit || 0,
              currency: 'USD',
            },
            clientExposure: {},
            supplierExposure: [
              {
                materialId: order.materialId,
                weightInProduct: 1,
                basePrice: 0,
                supplyLeadTime: 0,
                adjustment: 'none',
                exposureCalendar: [singleMonthExposure],
              },
            ],
            status: 'completed',
            updatedAt: Timestamp.now(),
            totalExposure: [
              {
                materialId: order.materialId,
                exposureCalendar: totalExposureCalendar,
              },
            ],
            monthExposure: { [order.materialId]: [singleMonthExposure] },
          };

          const result = await createFutureOrder(selectedAccountId, futureOrder);
          if (result?.error) {
            throw new Error(result.error);
          }

          successCount++;
          setSaveProgress((prev) => [
            ...prev,
            {
              message: `✓ ${order.customerName || order.materialId} ${monthDate.month}/${
                monthDate.year
              } saved (${orderIndex}/${totalOrdersToCreate})`,
              success: true,
            },
          ]);
        } catch (error) {
          errorCount++;
          setSaveProgress((prev) => [
            ...prev,
            {
              message: `✗ ${order.customerName || order.materialId} ${monthDate.month}/${
                monthDate.year
              } failed: ${error}`,
              success: false,
            },
          ]);
        }
      }
    }

    // Trigger exposure calculation
    try {
      await triggerExposureCalc({
        accountId: selectedAccountId,
        materialIds: Array.from(materialIds),
      });
      setSaveProgress((prev) => [...prev, { message: '✓ Exposure calculation triggered', success: true }]);
    } catch (error) {
      setSaveProgress((prev) => [...prev, { message: `✗ Exposure calculation failed: ${error}`, success: false }]);
    }

    setSaveProgress((prev) => [
      ...prev,
      {
        message: `\nCompleted: ${successCount} successful, ${errorCount} failed out of ${totalOrdersToCreate} total orders`,
        success: errorCount === 0,
      },
    ]);
  };

  const renderUploadStep = () => {
    const materialCol = Object.keys(columnMapping).find((key) => columnMapping[key] === 'materialName');
    const filteredData =
      materialFilter && materialCol
        ? excelData.filter((row) => row[materialCol]?.toString().toLowerCase().includes(materialFilter.toLowerCase()))
        : excelData;

    return (
      <div className="flex flex-col gap-6 p-6">
        {/* Progress indicator as chips with account selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-600 text-white font-semibold text-sm flex items-center gap-2">
              1. Upload & Map Columns
            </div>
            <div className="p-2 rounded-full bg-gray-200 text-gray-600 font-semibold text-sm">2. Map Values</div>
            <div className="p-2 rounded-full bg-gray-200 text-gray-600 font-semibold text-sm">3. Preview & Save</div>
          </div>

          {/* Clear Data Button */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setShowClearDataPopup(true)}
            startIcon={<OpenInNewIcon />}
          >
            Open Clear Data Popup
          </Button>

          {/* Account Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Import to Account:</span>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              size="small"
              displayEmpty
              className="min-w-[200px]"
            >
              <MenuItem value="" disabled>
                <em>Select Account</em>
              </MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.companyDetails?.name || acc.id}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Upload controls in one line */}
        <div className="flex gap-4 items-center">
          <label className="font-semibold whitespace-nowrap">Upload Type:</label>
          <Select value={uploadType} onChange={(e) => setUploadType(e.target.value as UploadType)} size="small">
            <MenuItem value="future-orders">Future Orders</MenuItem>
            <MenuItem value="mrp">MRP</MenuItem>
            <MenuItem value="pos">POs</MenuItem>
          </Select>
          <Button variant="contained" component="label" size="small">
            Browse File
            <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleFileUpload} />
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => {
              if (window.confirm('Are you sure you want to clear all data and start over?')) {
                clearAllLocalStorage();
                window.location.reload();
              }
            }}
          >
            Clear Uploaded File
          </Button>
          {file && <span className="text-gray-700 text-sm">{file.name}</span>}
          {loading && <CircularProgress size={20} />}
        </div>

        {/* Loading placeholder */}
        {loading && <LoadingSpinnerOverlay size={64}>Loading file data...</LoadingSpinnerOverlay>}

        {/* Data preview with column mapping dropdowns */}
        {!loading && excelData.length > 0 && (
          <>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Step 1: Map Columns to System Fields</h3>
                <div className="flex items-center gap-4">
                  {/* Sheet selector for Excel files */}
                  {sheetNames.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Sheet:</span>
                      <Select
                        value={selectedSheet}
                        onChange={(e) => handleSheetChange(e.target.value)}
                        size="small"
                        className="min-w-[150px]"
                      >
                        {sheetNames.map((sheet) => (
                          <MenuItem key={sheet} value={sheet}>
                            {sheet}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  )}
                  {uploadType === 'pos' && materialCol && (
                    <input
                      type="text"
                      placeholder="Filter by material name..."
                      value={materialFilter}
                      onChange={(e) => setMaterialFilter(e.target.value)}
                      className="border rounded px-3 py-1 text-sm w-64"
                    />
                  )}
                </div>
              </div>

              <div className="overflow-auto max-h-[500px] border rounded">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    {/* Column mapping dropdowns row */}
                    <tr className="bg-blue-350">
                      <th className="border px-2 py-2 text-left sticky left-0 bg-blue-350 z-20">
                        <span className="text-xs text-gray-600">Row #</span>
                      </th>
                      {headers.map((header) => (
                        <th key={header} className="border px-2 py-2 min-w-[180px]">
                          <Select
                            value={columnMapping[header] || ''}
                            onChange={(e) => setColumnMapping({ ...columnMapping, [header]: e.target.value })}
                            size="small"
                            displayEmpty
                            fullWidth
                            className="bg-white"
                          >
                            <MenuItem value="">
                              <em>Skip column</em>
                            </MenuItem>
                            {availableColumns.map((col) => (
                              <MenuItem key={col} value={col}>
                                {col}
                              </MenuItem>
                            ))}
                          </Select>
                        </th>
                      ))}
                    </tr>
                    {/* Original headers row */}
                    <tr>
                      <th className="border px-2 py-1 text-left sticky left-0 bg-gray-100 z-20">#</th>
                      {headers.map((header) => (
                        <th key={header} className="border px-2 py-1 text-left">
                          <div className="font-semibold">{header}</div>
                          {columnMapping[header] && (
                            <div className="text-xs text-blue-600 mt-1">→ {columnMapping[header]}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 sticky left-0 bg-white">{idx + 1}</td>
                        {headers.map((header) => {
                          const value = row[header];
                          let displayValue = '';

                          if (value instanceof Date) {
                            displayValue = value.toLocaleDateString();
                          } else if (value !== null && value !== undefined) {
                            displayValue = String(value);
                          }

                          return (
                            <td key={header} className="border px-2 py-1">
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600">
                Showing {Math.min(10, filteredData.length)} of {filteredData.length} rows
                {materialFilter && ` (filtered from ${excelData.length} total)`}
              </p>
            </div>

            <Button
              variant="contained"
              onClick={() => setStep('mapping')}
              className="self-start"
              disabled={Object.keys(columnMapping).filter((k) => columnMapping[k]).length === 0}
            >
              Next: Map Values (Materials, Units, Currencies)
            </Button>
          </>
        )}
      </div>
    );
  };

  const renderMappingStep = () => {
    const materialsToMap = getMaterialMappingNeeded();
    const currenciesToMap = getCurrencyMappingNeeded();
    const unitsToMap = getUnitMappingNeeded();
    const systemMaterials = Object.keys(materialsMap);
    const systemCurrencies = ['USD', 'EUR', 'GBP', 'ILS', 'CNY'];

    return (
      <div className="flex flex-col gap-6 p-6">
        {/* Progress indicator as chips with account selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-300 text-white font-semibold text-sm flex items-center gap-2">
              <span>✓</span> 1. Upload & Map Columns
            </div>
            <div className="p-2 rounded-full bg-blue-400 text-white font-semibold text-sm">2. Map Values</div>
            <div className="p-2 rounded-full bg-gray-200 text-gray-600 font-semibold text-sm">3. Preview & Save</div>
          </div>

          {/* Clear Data Button */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setShowClearDataPopup(true)}
            startIcon={<OpenInNewIcon />}
          >
            Open Clear Data Popup
          </Button>

          {/* Account Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Import to Account:</span>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              size="small"
              displayEmpty
              className="min-w-[200px]"
            >
              <MenuItem value="" disabled>
                <em>Select Account</em>
              </MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.companyDetails?.name || acc.id}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <h3 className="text-xl font-bold">Step 2: Map Values to System Data</h3>
        <p className="text-sm text-gray-600">
          Map materials, units, and currencies from your file to match the system values.
        </p>

        {materialsToMap.length > 0 && (
          <>
            <h3 className="text-lg font-bold mt-4 bg-gray-50 p-3 rounded">
              Materials Mapping ({materialsToMap.length} unique materials)
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              Match material names from your file to existing materials in the system, or skip irrelevant materials.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {materialsToMap.map((mat) => {
                const options = [
                  { label: 'Skip material', value: 'SKIP' },
                  ...systemMaterials.map((materialId) => ({
                    label: `${materialsMap[materialId]?.name || materialId} (${materialId})`,
                    value: materialId,
                  })),
                ];
                const selectedOption = options.find((opt) => opt.value === (materialMapping[mat] || 'SKIP'));

                return (
                  <div key={mat} className="flex items-center gap-1">
                    <span className="font-semibold text-sm w-1/3 truncate" title={mat}>
                      {mat}:
                    </span>
                    <Autocomplete
                      options={options}
                      value={selectedOption}
                      onChange={(_, newValue) => {
                        setMaterialMapping({ ...materialMapping, [mat]: newValue?.value || 'SKIP' });
                      }}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderInput={(params) => <TextField {...params} size="medium" placeholder="Skip material" />}
                      disableClearable
                      ListboxProps={{
                        style: {
                          maxHeight: '500px',
                        },
                      }}
                      componentsProps={{
                        paper: {
                          sx: {
                            width: '250px',
                            '& .MuiAutocomplete-listbox': {
                              '& .MuiAutocomplete-option[data-focus="true"]': {
                                backgroundColor: '#f3f4f6',
                              },
                            },
                          },
                        },
                      }}
                      className="w-[250px]"
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {unitsToMap.length > 0 && uploadType === 'pos' && (
          <>
            <h3 className="text-lg font-bold mt-4 bg-gray-50 p-3 rounded">
              Units Mapping ({unitsToMap.length} unique units)
            </h3>
            <p className="text-sm text-gray-600">
              Map unit values (e.g., KG, Ton) to standard mass units. KG = 0.001 MT.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {unitsToMap.map((unit) => (
                <div key={unit} className="flex items-center gap-2">
                  <span className="font-semibold w-1/3">{unit}:</span>
                  <Select
                    value={unitMapping[unit] || ''}
                    onChange={(e) => setUnitMapping({ ...unitMapping, [unit]: e.target.value as IMassUnit | 'kg' })}
                    className="w-2/3"
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select Unit</em>
                    </MenuItem>
                    {availableMassUnits.map((sysUnit) => (
                      <MenuItem key={sysUnit} value={sysUnit}>
                        {sysUnit === 'kg' ? 'kg (0.001 MT)' : sysUnit}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </>
        )}

        {currenciesToMap.length > 0 && uploadType === 'pos' && (
          <>
            <h3 className="text-lg font-bold mt-4 bg-gray-50 p-3 rounded">
              Currency Mapping ({currenciesToMap.length} unique currencies)
            </h3>
            <p className="text-sm text-gray-600">Map currency codes from your file to standard currency codes.</p>
            <div className="grid grid-cols-2 gap-4">
              {currenciesToMap.map((curr) => (
                <div key={curr} className="flex items-center gap-2">
                  <span className="font-semibold w-1/3">{curr}:</span>
                  <Select
                    value={currencyMapping[curr] || ''}
                    onChange={(e) => setCurrencyMapping({ ...currencyMapping, [curr]: e.target.value })}
                    className="w-2/3"
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select Currency</em>
                    </MenuItem>
                    {systemCurrencies.map((sysCurr) => (
                      <MenuItem key={sysCurr} value={sysCurr}>
                        {sysCurr}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-4 mt-4">
          <Button variant="outlined" onClick={() => setStep('upload')}>
            Back
          </Button>
          <Button variant="contained" onClick={() => setStep('preview')}>
            Preview
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => {
              if (window.confirm('Are you sure you want to clear all data and start over?')) {
                clearAllLocalStorage();
                window.location.reload();
              }
            }}
          >
            Clear Uploaded File
          </Button>
        </div>
      </div>
    );
  };

  const renderPreviewStep = () => {
    const processedData = processData();
    const totalPages = Math.ceil(processedData.length / ROWS_PER_PAGE);
    const startIdx = previewPage * ROWS_PER_PAGE;
    const endIdx = startIdx + ROWS_PER_PAGE;
    const paginatedData = processedData.slice(startIdx, endIdx);

    // Determine collection name based on upload type
    const getCollectionInfo = () => {
      switch (uploadType) {
        case 'pos':
          return {
            name: 'Purchase Orders (POs)',
            description: 'Data will be saved to Inventory Reports collection with isPO flag',
          };
        case 'mrp':
          return {
            name: 'MRP (Material Requirements Planning)',
            description: 'Data will be saved to Future Orders collection with MRP delivery type',
          };
        case 'future-orders':
          return {
            name: 'Future Orders',
            description: 'Data will be saved to Future Orders collection',
          };
        default:
          return { name: '', description: '' };
      }
    };

    const collectionInfo = getCollectionInfo();

    return (
      <div className="flex flex-col gap-4 p-6">
        {/* Progress indicator as chips with account selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-300 text-white font-semibold text-sm flex items-center gap-2">
              <span>✓</span> 1. Upload & Map Columns
            </div>
            <div className="p-2 rounded-full bg-green-300 text-white font-semibold text-sm flex items-center gap-2">
              <span>✓</span> 2. Map Values
            </div>
            <div className="p-2 rounded-full bg-blue-600 text-white font-semibold text-sm">3. Preview & Save</div>
          </div>

          {/* Clear Data Button */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setShowClearDataPopup(true)}
            startIcon={<OpenInNewIcon />}
          >
            Open Clear Data Popup
          </Button>

          {/* Account Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Import to Account:</span>
            <Select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              size="small"
              displayEmpty
              className="min-w-[200px]"
            >
              <MenuItem value="" disabled>
                <em>Select Account</em>
              </MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.companyDetails?.name || acc.id}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Collection info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-blue-600 text-2xl">ℹ️</div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Saving to: {collectionInfo.name}</h4>
              <p className="text-sm text-blue-800">{collectionInfo.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold">Preview Data ({processedData.length} rows total)</h3>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPreviewPage(Math.max(0, previewPage - 1))}
                disabled={previewPage === 0}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {previewPage + 1} of {totalPages}
              </span>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPreviewPage(Math.min(totalPages - 1, previewPage + 1))}
                disabled={previewPage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        <div className="overflow-auto max-h-[600px] border rounded">
          {uploadType === 'pos' && (
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border p-2 sticky left-0 bg-gray-100 z-10">#</th>
                  <th className="border p-2 sticky left-10 bg-gray-100 z-10">Material</th>
                  <th className="border p-2">PO ID</th>
                  <th className="border p-2">Batch ID</th>
                  <th className="border p-2">Supplier</th>
                  <th className="border p-2">Product ID</th>
                  <th className="border p-2">Expected Date</th>
                  <th className="border p-2">Original Quantity</th>
                  <th className="border p-2">Unit</th>
                  <th className="border p-2">Converted quantity (MT)</th>
                  <th className="border ">Delivered Qty</th>
                  <th className="border p-2">Price per ton</th>
                  <th className="border p-2">Ratio</th>
                  <th className="border p-2">Currency</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, idx) => {
                  const formatDate = (value: any) =>
                    value instanceof Date
                      ? value.toLocaleDateString('en-CA')
                      : new Date(value).toLocaleDateString('en-CA');

                  const formatValue = (value: any) => {
                    if (value instanceof Date) {
                      const year = value.getFullYear();
                      const month = String(value.getMonth() + 1).padStart(2, '0');
                      const day = String(value.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    }
                    if (value === null || value === undefined) {
                      return '';
                    }
                    return String(value);
                  };

                  // Check if price is less than 9 - likely a ratio
                  const rawPrice = Number(row.actualPrice) || 0;
                  const isRatioPrice = rawPrice > 0 && rawPrice < 9;

                  // Calculate price per ton: multiply by 1000 if unit is kg
                  const pricePerTon =
                    !isRatioPrice && row.systemUnit === 'kg' ? Math.floor(rawPrice * 1000) : Math.floor(rawPrice);

                  return (
                    <tr key={idx}>
                      <td className="border p-2 sticky left-0 bg-white">{row.rowNum}</td>
                      <td className="border p-2 sticky left-10 bg-white">{formatValue(row.materialId)}</td>
                      <td className="border p-2">{formatValue(row.poId)}</td>
                      <td className="border px-2 py-1">
                        <input
                          type="text"
                          value={batchIdEdits[row.rowNum] !== undefined ? batchIdEdits[row.rowNum] : row.batchId || ''}
                          onChange={(e) => {
                            setBatchIdEdits({ ...batchIdEdits, [row.rowNum]: e.target.value });
                          }}
                          className="w-full px-2 py-1 border rounded"
                          placeholder="Batch ID"
                        />
                      </td>
                      <td className="border p-2">{formatValue(row.supplier)}</td>
                      <td className="border p-2">{formatValue(row.productId)}</td>
                      <td className="border p-2">{formatDate(row.expectedDate)}</td>
                      <td className="border p-2">{formatValue(row.quantity)}</td>
                      <td className="border p-2">{formatValue(row.systemUnit)}</td>
                      <td className="border p-2">{row.convertedQuantity ? Math.floor(row.convertedQuantity) : ''}</td>
                      <td className="border p-2">{formatValue(row.deliveredQuantity)}</td>
                      <td className="border p-2">{isRatioPrice ? '-' : pricePerTon}</td>
                      <td className="border p-2">{isRatioPrice ? rawPrice : formatValue(row.ratioNum) || '-'}</td>
                      <td className="border p-2">{formatValue(row.actualCurrency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {uploadType === 'mrp' && (
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border p-2 sticky left-0 bg-gray-100 z-10">#</th>
                  <th className="border p-2 sticky left-10 bg-gray-100 z-10">Material</th>
                  <th className="border p-2 sticky left-20 bg-gray-100 z-10">Customer</th>
                  <th className="border p-2 sticky left-[120px] bg-gray-100 z-10">Price per MT</th>
                  {FUTURE_ORDER_COLUMNS.slice(3).map((col) => (
                    <th key={col} className="border p-2">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, idx) => {
                  const formatValue = (value: any) => {
                    if (value instanceof Date) {
                      return value.toLocaleDateString();
                    }
                    if (value === null || value === undefined) {
                      return '';
                    }
                    return String(value);
                  };

                  return (
                    <tr key={idx}>
                      <td className="border p-2 sticky left-0 bg-white">{row.rowNum}</td>
                      <td className="border p-2 sticky left-10 bg-white">{formatValue(row.materialId)}</td>
                      <td className="border p-2 sticky left-20 bg-white">
                        <input
                          type="text"
                          value={customerNameEdits[row.rowNum] || row.customerName || ''}
                          onChange={(e) => {
                            setCustomerNameEdits({
                              ...customerNameEdits,
                              [row.rowNum]: e.target.value.slice(0, 20),
                            });
                          }}
                          maxLength={20}
                          className="w-full px-2 py-1 border rounded"
                          placeholder="Customer name"
                        />
                      </td>
                      <td className="border p-2 sticky left-[120px] bg-white">
                        <input
                          type="number"
                          value={priceEdits[row.rowNum] !== undefined ? priceEdits[row.rowNum] : row.pricePerUnit || ''}
                          onChange={(e) => {
                            setPriceEdits({
                              ...priceEdits,
                              [row.rowNum]: Number(e.target.value),
                            });
                          }}
                          style={{ width: '200px' }}
                          className="px-2 py-1 border rounded"
                          placeholder="Price"
                        />
                      </td>
                      {FUTURE_ORDER_COLUMNS.slice(3).map((col) => (
                        <td key={col} className="border p-2">
                          {formatValue(row[col])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {uploadType === 'future-orders' && (
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border p-2 sticky left-0 bg-gray-100 z-10">#</th>
                  <th className="border p-2 sticky left-10 bg-gray-100 z-10">Material</th>
                  <th className="border p-2 sticky left-20 bg-gray-100 z-10">Customer</th>
                  <th className="border p-2 sticky left-30 bg-gray-100 z-10">Price per MT</th>
                  {FUTURE_ORDER_COLUMNS.slice(3).map((col) => (
                    <th key={col} className="border p-2">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, idx) => {
                  const formatValue = (value: any) => {
                    if (value instanceof Date) {
                      return value.toLocaleDateString();
                    }
                    if (value === null || value === undefined) {
                      return '';
                    }
                    return String(value);
                  };

                  return (
                    <tr key={idx}>
                      <td className="border p-2 sticky left-0 bg-white">{row.rowNum}</td>
                      <td className="border p-2 sticky left-10 bg-white">{formatValue(row.materialId)}</td>
                      <td className="border p-2 sticky left-20 bg-white">
                        <input
                          type="text"
                          value={customerNameEdits[row.rowNum] || row.customerName || ''}
                          onChange={(e) => {
                            setCustomerNameEdits({
                              ...customerNameEdits,
                              [row.rowNum]: e.target.value.slice(0, 20),
                            });
                          }}
                          maxLength={20}
                          className="w-32 p-1 border rounded"
                          placeholder="Customer name"
                        />
                      </td>
                      <td className="border p-2 sticky left-30 bg-white">
                        <input
                          type="number"
                          value={priceEdits[row.rowNum] !== undefined ? priceEdits[row.rowNum] : row.pricePerUnit || ''}
                          onChange={(e) => {
                            setPriceEdits({
                              ...priceEdits,
                              [row.rowNum]: Number(e.target.value),
                            });
                          }}
                          className="p-1 border rounded w-32"
                          placeholder="Price"
                        />
                      </td>
                      {FUTURE_ORDER_COLUMNS.slice(3).map((col) => (
                        <td key={col} className="border p-2">
                          {formatValue(row[col])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPreviewPage(Math.max(0, previewPage - 1))}
              disabled={previewPage === 0}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {previewPage + 1} of {totalPages} (Rows {startIdx + 1}-{Math.min(endIdx, processedData.length)} of{' '}
              {processedData.length})
            </span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPreviewPage(Math.min(totalPages - 1, previewPage + 1))}
              disabled={previewPage >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        )}

        <div className="flex gap-4 mt-4">
          <Button
            variant="outlined"
            onClick={() => {
              setStep('mapping');
              setPreviewPage(0);
            }}
          >
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save to Database'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => {
              if (window.confirm('Are you sure you want to clear all data and start over?')) {
                clearAllLocalStorage();
                window.location.reload();
              }
            }}
          >
            Clear Uploaded File
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full">
      {step === 'upload' && renderUploadStep()}
      {step === 'mapping' && renderMappingStep()}
      {step === 'preview' && renderPreviewStep()}

      <SaveProgressDialog
        open={saveProgressOpen}
        savingComplete={savingComplete}
        saveProgress={saveProgress}
        onClose={() => setSaveProgressOpen(false)}
      />

      {showClearDataPopup && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowClearDataPopup(false)}>
          <ClearDataPopup accounts={accounts} />
        </OverlayDisplayDiv>
      )}
    </Card>
  );
};
